<template>
  <v-dialog
    :model-value="show"
    max-width="500"
    persistent
    @update:model-value="$emit('update:show', $event)"
  >
    <v-card>
      <v-card-title class="d-flex align-center bg-primary-lighten-5">
        <v-icon start>mdi-account-filter</v-icon>
        Contacten filteren
        <v-spacer />
        <v-btn icon variant="text" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <!-- Categories filter section -->
        <div class="filter-section mb-4">
          <h4 class="text-h6 mb-3 text-grey-darken-2">
            <v-icon start size="20">mdi-tag-multiple</v-icon>
            Categorieën
          </h4>

          <v-row class="mb-2">
            <v-col cols="12">
              <v-select
                v-model="localFilters.categoryIds"
                label="Filter op categorieën"
                :items="categoryOptions"
                item-title="title"
                item-value="value"
                variant="outlined"
                density="comfortable"
                multiple
                chips
                closable-chips
                clearable
                hide-details
                :loading="categoriesLoading"
                prepend-inner-icon="mdi-tag-multiple"
              >
                <template #chip="{ props: chipProps, item }">
                  <v-chip
                    v-bind="chipProps"
                    :color="item.raw.color"
                    variant="tonal"
                    size="small"
                    closable
                  >
                    <template #prepend>
                      <v-icon size="small" class="mr-1">{{
                        item.raw.icon
                      }}</v-icon>
                    </template>
                    {{ item.raw.title }}
                  </v-chip>
                </template>
                <template #item="{ props: itemProps, item }">
                  <v-list-item v-bind="itemProps">
                    <template #prepend>
                      <v-checkbox-btn
                        :model-value="
                          localFilters.categoryIds?.includes(item.raw.value)
                        "
                      />
                      <v-icon :color="item.raw.color" class="ml-2">{{
                        item.raw.icon
                      }}</v-icon>
                    </template>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </div>

        <div class="filter-section mb-4">
          <h4 class="text-h6 mb-3 text-grey-darken-2">
            <v-icon start size="20">mdi-filter-variant</v-icon>
            Contact Details
          </h4>

          <!-- Email filter -->
          <v-row class="mb-2">
            <v-col cols="12">
              <v-select
                v-model="localFilters.hasEmail"
                label="E-mail adres"
                :items="emailOptions"
                variant="outlined"
                density="comfortable"
                clearable
                hide-details
                prepend-inner-icon="mdi-email"
              />
            </v-col>
          </v-row>

          <!-- Phone filter -->
          <v-row class="mb-2">
            <v-col cols="12">
              <v-select
                v-model="localFilters.hasPhone"
                label="Telefoonnummer"
                :items="phoneOptions"
                variant="outlined"
                density="comfortable"
                clearable
                hide-details
                prepend-inner-icon="mdi-phone"
              />
            </v-col>
          </v-row>

          <!-- Address filter -->
          <v-row class="mb-2">
            <v-col cols="12">
              <v-select
                v-model="localFilters.hasAddress"
                label="Adres"
                :items="addressOptions"
                variant="outlined"
                density="comfortable"
                clearable
                hide-details
                prepend-inner-icon="mdi-map-marker"
              />
            </v-col>
          </v-row>
        </div>

        <!-- Active filters summary -->
        <div v-if="activeFilterCount > 0" class="filter-summary">
          <v-divider class="mb-3" />
          <h5 class="text-subtitle-1 mb-2 text-grey-darken-2">
            Actieve filters ({{ activeFilterCount }})
          </h5>
          <div class="d-flex flex-wrap gap-2">
            <!-- Category filter chips -->
            <v-chip
              v-for="catId in localFilters.categoryIds || []"
              :key="`cat-${catId}`"
              size="small"
              :color="getCategoryColor(catId)"
              variant="tonal"
              closable
              @click:close="removeCategoryFilter(catId)"
            >
              <v-icon start size="16">{{ getCategoryIcon(catId) }}</v-icon>
              {{ getCategoryName(catId) }}
            </v-chip>

            <v-chip
              v-if="localFilters.hasEmail !== null"
              size="small"
              color="primary"
              variant="tonal"
              closable
              @click:close="localFilters.hasEmail = null"
            >
              <v-icon start size="16">mdi-email</v-icon>
              {{ localFilters.hasEmail ? "Met e-mail" : "Zonder e-mail" }}
            </v-chip>

            <v-chip
              v-if="localFilters.hasPhone !== null"
              size="small"
              color="primary"
              variant="tonal"
              closable
              @click:close="localFilters.hasPhone = null"
            >
              <v-icon start size="16">mdi-phone</v-icon>
              {{ localFilters.hasPhone ? "Met telefoon" : "Zonder telefoon" }}
            </v-chip>

            <v-chip
              v-if="localFilters.hasAddress !== null"
              size="small"
              color="primary"
              variant="tonal"
              closable
              @click:close="localFilters.hasAddress = null"
            >
              <v-icon start size="16">mdi-map-marker</v-icon>
              {{ localFilters.hasAddress ? "Met adres" : "Zonder adres" }}
            </v-chip>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="pa-4 pt-0">
        <v-btn
          variant="outlined"
          color="error"
          :disabled="activeFilterCount === 0"
          @click="clearAllFilters"
        >
          <v-icon start>mdi-filter-remove</v-icon>
          Alles wissen
        </v-btn>

        <v-spacer />

        <v-btn variant="outlined" @click="closeDialog"> Annuleren </v-btn>

        <v-btn color="primary" variant="elevated" @click="applyFilters">
          <v-icon start>mdi-check</v-icon>
          Filters toepassen
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useContactCategoriesStoreRefactor } from "@/stores/contactCategoriesStoreRefactor";

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      hasEmail: null,
      hasPhone: null,
      hasAddress: null,
      categoryIds: [],
    }),
  },
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:show",
  "update:modelValue",
  "apply-filters",
  "clear-filters",
]);

// Store
const categoriesStore = useContactCategoriesStoreRefactor();

// Local reactive copy of filters
const localFilters = ref({
  ...props.modelValue,
  categoryIds: props.modelValue.categoryIds || [],
});

// Loading state
const categoriesLoading = computed(() => categoriesStore.loading);

// Category options for select
const categoryOptions = computed(() => {
  return categoriesStore.categories.map((cat) => ({
    title: cat.name,
    value: cat.id,
    icon: cat.icon || "mdi-folder",
    color: cat.color || "primary",
  }));
});

// Filter options
const emailOptions = [
  { title: "Met e-mail adres", value: true },
  { title: "Zonder e-mail adres", value: false },
];

const phoneOptions = [
  { title: "Met telefoonnummer", value: true },
  { title: "Zonder telefoonnummer", value: false },
];

const addressOptions = [
  { title: "Met adres", value: true },
  { title: "Zonder adres", value: false },
];

// Computed properties
const activeFilterCount = computed(() => {
  let count = 0;
  if (localFilters.value.categoryIds?.length > 0)
    count += localFilters.value.categoryIds.length;
  if (localFilters.value.hasEmail !== null) count++;
  if (localFilters.value.hasPhone !== null) count++;
  if (localFilters.value.hasAddress !== null) count++;
  return count;
});

// Watch for external changes to modelValue
watch(
  () => props.modelValue,
  (newValue) => {
    localFilters.value = {
      ...newValue,
      categoryIds: newValue.categoryIds || [],
    };
  },
  { deep: true },
);

// Helper methods for category display
const getCategoryName = (id) => {
  const cat = categoriesStore.getCategoryById(id);
  return cat?.name || "Onbekend";
};

const getCategoryIcon = (id) => {
  const cat = categoriesStore.getCategoryById(id);
  return cat?.icon || "mdi-folder";
};

const getCategoryColor = (id) => {
  const cat = categoriesStore.getCategoryById(id);
  return cat?.color || "primary";
};

const removeCategoryFilter = (id) => {
  localFilters.value.categoryIds = localFilters.value.categoryIds.filter(
    (catId) => catId !== id,
  );
};

// Methods
const closeDialog = () => {
  emit("update:show", false);
};

const applyFilters = () => {
  emit("apply-filters", { ...localFilters.value });
  emit("update:modelValue", { ...localFilters.value });
};

const clearAllFilters = () => {
  localFilters.value = {
    hasEmail: null,
    hasPhone: null,
    hasAddress: null,
    categoryIds: [],
  };
  emit("clear-filters");
};

// Load categories on mount
onMounted(async () => {
  await categoriesStore.fetchAll();
});
</script>

<style scoped>
.filter-section {
  border-left: 3px solid rgb(var(--v-theme-primary));
  padding-left: 16px;
}

.filter-summary {
  background-color: rgb(var(--v-theme-surface-variant));
  border-radius: 8px;
  padding: 16px;
}

.gap-2 {
  gap: 8px;
}
</style>
